/**
 * WorkshopNSchedule — körschema-editor för WorkshopN.
 * AI läser brief + workshopkonfiguration och genererar ett körschema.
 * Facilitatorn redigerar och sparar. Helskärm via /workshopn/:id/schedule-public.
 *
 * Route: /workshopn/:id/schedule
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Paper, CircularProgress,
  IconButton, Tooltip, Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TvIcon from '@mui/icons-material/Tv';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { db } from '../../../firebase/config';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { callClaude } from '../../../utils/claudeProxy';

const accent = '#E05500';

const SYSTEM_PROMPT = `Du är en assistent som hjälper workshopledare att planera dagordningen för en workshop.
Du tar emot information om workshopen och returnerar ENDAST ett JSON-objekt — ingen annan text.

JSON-struktur:
{
  "blocks": [
    {
      "id": "block_1",
      "time": "14:15–14:25",
      "duration": 10,
      "title": "Etablering & intro",
      "description": "",
      "subItems": [
        { "text": "Välkomstal + presentation", "duration": 5 },
        { "text": "QR-inloggning och gruppindelning", "duration": 5 }
      ]
    }
  ],
  "questionTimers": [5, 5]
}

Riktlinjer:
- Skapa 5–9 block baserat på workshopens upplägg
- Tidsintervallet ska passa workshopens längd och startid
- duration är antal minuter som ett heltal (summan av subItems duration ska stämma med blockets duration)
- Titlarna ska vara korta och tydliga (max 5 ord)
- Inkludera pauser, fika och sociala moment om de nämns i briefen
- Varje block ska ha 2–5 subItems (delmoment) med exakta minuttider
- För diskussionsblock: räkna ut tid per grupp baserat på antal grupper och fördela jämnt
- För frågeblock: räkna tid per fråga och grupp (t.ex. 3 grupper × 2 frågor = 6 × X min)
- För pauser/intro/avslut: enklare subItems med logistik och innehåll
- questionTimers: en array med en timer-tid (minuter) per fråga i samma ordning som frågorna skickades in.
  Räkna ut hur lång tid varje diskussionsrunda per fråga bör vara baserat på antal grupper och total tid.
  Exempel: 3 grupper, 2 frågor, 40 min diskussionstid totalt → 40/(3×2) ≈ 7 min per fråga per grupp.
- Returnera BARA JSON, inga förklaringar`;

function buildInput(session, brief, materials) {
  const groups    = session.config?.groups    || [];
  const questions = session.config?.questions || [];

  // Calculate participant count across all groups
  const totalParticipants = groups.reduce((sum, g) => sum + (g.members?.length || 0), 0);

  let text = `Workshop: "${session.title}"\n`;
  if (session.date) text += `Datum: ${session.date}\n`;
  if (groups.length) {
    text += `Grupper (${groups.length} st): ${groups.map(g => {
      const count = g.members?.length || 0;
      return count > 0 ? `${g.name} (${count} pers)` : g.name;
    }).join(', ')}\n`;
  }
  if (totalParticipants > 0) text += `Totalt deltagare: ${totalParticipants}\n`;
  if (questions.length) {
    text += `Frågor (${questions.length} st):\n${questions.map((q, i) => `  ${i + 1}. ${q.text}`).join('\n')}\n`;
  }
  if (brief) text += `\nBrief:\n${brief}\n`;
  if (materials?.length) {
    const textMats = materials.filter(m => m.content);
    if (textMats.length) {
      text += `\nKontextmaterial:\n${textMats.map(m => `[${m.name || ''}]\n${m.content}`).join('\n\n')}\n`;
    }
  }
  return text;
}

function newBlock(index) {
  return {
    id: `block_${Date.now()}_${index}`,
    time: '',
    duration: 15,
    title: '',
    description: '',
  };
}

export default function WorkshopNSchedule() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [session,        setSession]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [blocks,         setBlocks]         = useState([]);
  const [materials,      setMaterials]      = useState([]);
  const [currentBlock,   setCurrentBlock]   = useState(0);
  const [generating,     setGenerating]     = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [saved,          setSaved]          = useState(false);
  const [error,          setError]          = useState('');
  const [expandedBlocks,  setExpandedBlocks]  = useState({});
  const [questionTimers,  setQuestionTimers]  = useState([]);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, 'workshops', id)),
      getDocs(collection(db, 'workshops', id, 'contextMaterials')),
    ]).then(([snap, matsSnap]) => {
      if (!snap.exists()) return navigate('/workshopn');
      const d = snap.data();
      setSession(d);
      const loadedBlocks = d.schedule?.blocks || [];
      setBlocks(loadedBlocks);
      setCurrentBlock(d.schedule?.currentBlock ?? 0);
      // Auto-expand blocks that already have sub-items
      const expanded = {};
      loadedBlocks.forEach((b, i) => { if (b.subItems?.length > 0) expanded[i] = true; });
      setExpandedBlocks(expanded);
      setMaterials(matsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [id, navigate]);

  const generate = async () => {
    if (!session) return;
    setGenerating(true);
    setError('');
    try {
      const brief = session.aiContext?.brief || '';
      const input = buildInput(session, brief, materials);
      const data = await callClaude({
        model: 'claude-sonnet-4-6',
        max_tokens: 2000,
        system: SYSTEM_PROMPT,
        messages: [{ role: 'user', content: input }],
      });
      const raw   = data.content?.[0]?.text || '';
      const clean = raw.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);
      const generated = parsed.blocks || [];
      setBlocks(generated);
      if (parsed.questionTimers?.length) setQuestionTimers(parsed.questionTimers);
      const expanded = {};
      generated.forEach((_, i) => { expanded[i] = true; });
      setExpandedBlocks(expanded);
    } catch {
      setError('Genereringen misslyckades.');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const update = { schedule: { blocks, currentBlock: 0, isLive: false } };
      // Write AI-suggested question timers as timerDurations (seconds) if present
      if (questionTimers.length) {
        const durations = {};
        questionTimers.forEach((mins, i) => { durations[i] = mins * 60; });
        update.timerDurations = durations;
      }
      await updateDoc(doc(db, 'workshops', id), update);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const updateBlock = (i, field, val) =>
    setBlocks(b => b.map((x, idx) => idx === i ? { ...x, [field]: val } : x));
  const deleteBlock = (i) => setBlocks(b => b.filter((_, idx) => idx !== i));

  const updateSubItem = (blockIdx, subIdx, field, val) =>
    setBlocks(b => b.map((x, i) => i !== blockIdx ? x : {
      ...x,
      subItems: (x.subItems || []).map((s, j) => j === subIdx ? { ...s, [field]: val } : s),
    }));
  const deleteSubItem = (blockIdx, subIdx) =>
    setBlocks(b => b.map((x, i) => i !== blockIdx ? x : {
      ...x,
      subItems: (x.subItems || []).filter((_, j) => j !== subIdx),
    }));
  const addSubItem = (blockIdx) =>
    setBlocks(b => b.map((x, i) => i !== blockIdx ? x : {
      ...x,
      subItems: [...(x.subItems || []), { text: '', duration: 5 }],
    }));
  const toggleExpand = (i) =>
    setExpandedBlocks(prev => ({ ...prev, [i]: !prev[i] }));
  const markCurrent = async (i) => {
    setCurrentBlock(i);
    await updateDoc(doc(db, 'workshops', id), { 'schedule.currentBlock': i });
  };

  const moveBlock   = (i, dir) => {
    const next = i + dir;
    if (next < 0 || next >= blocks.length) return;
    setBlocks(b => {
      const arr = [...b];
      [arr[i], arr[next]] = [arr[next], arr[i]];
      return arr;
    });
  };

  const brief = session?.aiContext?.brief || '';
  const hasBrief = !!brief.trim();

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', p: { xs: 2, md: 4 }, maxWidth: 820, mx: 'auto' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate(`/workshopn/${id}/edit`)} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Körschema</Typography>
          {session?.title && <Typography fontSize="0.78rem" color="text.secondary">{session.title}</Typography>}
        </Box>

        {blocks.length > 0 && (
          <>
            <Button
              variant="outlined"
              startIcon={<TvIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`/workshopn/${id}/schedule-speaker`, '_blank')}
              sx={{ textTransform: 'none', fontSize: '0.78rem', borderColor: '#42A5F5', color: '#42A5F5' }}
            >
              Kör körschema
            </Button>
            <Button
              variant="outlined"
              startIcon={<TvIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`/workshopn/${id}/schedule-public`, '_blank')}
              sx={{ textTransform: 'none', fontSize: '0.78rem', borderColor: accent, color: accent }}
            >
              Öppna helskärm
            </Button>
          </>
        )}

        <Button
          variant="contained"
          onClick={save}
          disabled={saving || blocks.length === 0}
          sx={{
            bgcolor: saved ? '#4ade80' : accent,
            '&:hover': { bgcolor: saved ? '#22c55e' : '#C94D00' },
            textTransform: 'none', fontWeight: 600, minWidth: 100,
            transition: 'background-color 0.3s',
          }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : saved ? 'Sparat ✓' : 'Spara'}
        </Button>
      </Box>

      {/* Brief summary + generate */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={600} mb={0.5}>Generera körschema med AI</Typography>
            <Typography fontSize="0.78rem" color="text.secondary">
              {hasBrief
                ? 'AI läser briefen och workshopkonfigurationen och skapar ett förslag på körschema.'
                : 'Inget brief är lagt till ännu. Lägg till ett brief under Inställningar → AI-kontext för bästa resultat. Du kan ändå generera ett grundschema baserat på frågorna.'}
            </Typography>
            {hasBrief && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                <Typography fontSize="0.7rem" color="text.disabled" fontWeight={700} letterSpacing={0.5} mb={0.5}>BRIEF</Typography>
                <Typography fontSize="0.78rem" color="text.secondary" sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                  {brief.length > 300 ? brief.slice(0, 300) + '…' : brief}
                </Typography>
              </Box>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={generating
              ? <CircularProgress size={16} sx={{ color: '#fff' }} />
              : <AutoAwesomeIcon />}
            disabled={generating}
            onClick={generate}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
          >
            {generating ? 'Genererar...' : blocks.length > 0 ? 'Regenerera' : 'Generera'}
          </Button>
        </Box>
        {error && <Typography fontSize="0.8rem" color="error.main" mt={1.5}>{error}</Typography>}

        {/* Question timers preview */}
        {questionTimers.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography fontSize="0.7rem" color="text.disabled" fontWeight={700} letterSpacing={0.5} mb={1}>
              FÖRESLAGNA TIMERS PER FRÅGA — sparas automatiskt till presentationsläget
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {questionTimers.map((mins, i) => {
                const q = session?.config?.questions?.[i];
                return (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 0.75, borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid', borderColor: 'divider',
                  }}>
                    <Typography fontSize="0.7rem" color="text.disabled" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {q ? `F${i + 1}: ${q.text.slice(0, 40)}${q.text.length > 40 ? '…' : ''}` : `Fråga ${i + 1}`}
                    </Typography>
                    <TextField
                      size="small" type="number" value={mins}
                      onChange={e => setQuestionTimers(prev => prev.map((v, j) => j === i ? Number(e.target.value) : v))}
                      inputProps={{ min: 1, max: 60 }}
                      sx={{ width: 64, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.4, textAlign: 'center' } }}
                    />
                    <Typography fontSize="0.7rem" color="text.disabled">min</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Block list */}
      {blocks.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography fontWeight={600} sx={{ flex: 1 }}>{blocks.length} block</Typography>
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => setBlocks(b => [...b, newBlock(b.length)])}
              sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { color: accent } }}>
              Lägg till block
            </Button>
          </Box>

          {blocks.map((block, i) => (
            <Box
              key={block.id || i}
              sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}
            >
              {/* Live-markering */}
              <Tooltip title={i === currentBlock ? 'Aktuellt block på helskärm' : 'Klicka för att markera som aktuellt på helskärm'}>
                <Box
                  onClick={() => markCurrent(i)}
                  sx={{
                    width: 8, alignSelf: 'stretch', borderRadius: 4, flexShrink: 0, mt: 0.5,
                    bgcolor: i === currentBlock ? accent : 'rgba(255,255,255,0.06)',
                    cursor: 'pointer', transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: i === currentBlock ? accent : accent + '44' },
                    minHeight: 40,
                  }}
                />
              </Tooltip>
              {/* Move buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pt: 0.5, flexShrink: 0 }}>
                <Tooltip title="Flytta upp">
                  <span>
                    <IconButton size="small" disabled={i === 0} onClick={() => moveBlock(i, -1)}
                      sx={{ p: 0.25, color: 'text.disabled', '&:not(:disabled):hover': { color: accent } }}>
                      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 16, mx: 'auto' }} />
                <Tooltip title="Flytta ner">
                  <span>
                    <IconButton size="small" disabled={i === blocks.length - 1} onClick={() => moveBlock(i, 1)}
                      sx={{ p: 0.25, color: 'text.disabled', '&:not(:disabled):hover': { color: accent } }}>
                      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {/* Block card */}
              <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.75, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.25, alignItems: 'center' }}>
                  {/* Block number */}
                  <Box sx={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    bgcolor: accent + '20', border: `1px solid ${accent}44`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography fontSize="0.65rem" fontWeight={800} color={accent}>{i + 1}</Typography>
                  </Box>
                  {/* Title */}
                  <TextField
                    size="small" placeholder="Blocktitel" value={block.title}
                    onChange={e => updateBlock(i, 'title', e.target.value)}
                    sx={{ flex: 1, '& .MuiInputBase-root': { fontWeight: 600 } }}
                  />
                  {/* Delete */}
                  <IconButton size="small" onClick={() => deleteBlock(i)}
                    sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, mb: (block.subItems?.length > 0 || expandedBlocks[i]) ? 1.25 : 0 }}>
                  <TextField
                    size="small" label="Tid" placeholder="14:15–14:25"
                    value={block.time} onChange={e => updateBlock(i, 'time', e.target.value)}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    size="small" label="Minuter" type="number"
                    value={block.duration} onChange={e => updateBlock(i, 'duration', Number(e.target.value))}
                    sx={{ width: 100 }}
                    inputProps={{ min: 1, max: 180 }}
                  />
                  <TextField
                    size="small" label="Beskrivning (valfritt)" placeholder="Kort notering..."
                    value={block.description || ''} onChange={e => updateBlock(i, 'description', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Box>

                {/* Sub-items toggle */}
                <Box
                  onClick={() => toggleExpand(i)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    color: 'text.disabled', mt: 0.5,
                    '&:hover': { color: 'text.secondary' },
                  }}
                >
                  {expandedBlocks[i] ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                  <Typography fontSize="0.65rem" fontWeight={700} letterSpacing={0.5}>
                    DELMOMENT {block.subItems?.length > 0 ? `(${block.subItems.length})` : ''}
                  </Typography>
                </Box>

                {/* Sub-items list */}
                <Collapse in={!!expandedBlocks[i]}>
                  <Box sx={{ mt: 1, pl: 1, borderLeft: `2px solid ${accent}33` }}>
                    {(block.subItems || []).map((sub, j) => (
                      <Box key={j} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'center' }}>
                        <Typography fontSize="0.65rem" color="text.disabled" sx={{ minWidth: 16, textAlign: 'right', flexShrink: 0 }}>
                          {j + 1}.
                        </Typography>
                        <TextField
                          size="small" placeholder="Delmoment..." value={sub.text}
                          onChange={e => updateSubItem(i, j, 'text', e.target.value)}
                          sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.5 } }}
                        />
                        <TextField
                          size="small" type="number" value={sub.duration}
                          onChange={e => updateSubItem(i, j, 'duration', Number(e.target.value))}
                          inputProps={{ min: 1, max: 60 }}
                          sx={{ width: 72, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.5 } }}
                          InputProps={{ endAdornment: <Typography fontSize="0.65rem" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>min</Typography> }}
                        />
                        <IconButton size="small" onClick={() => deleteSubItem(i, j)}
                          sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                      onClick={() => addSubItem(i)}
                      sx={{ textTransform: 'none', fontSize: '0.7rem', color: 'text.disabled', p: 0.5, mt: 0.25, '&:hover': { color: accent } }}>
                      Lägg till delmoment
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          ))}

          {/* Bottom actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2, pb: 4 }}>
            <Button
              variant="outlined"
              startIcon={<TvIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`/workshopn/${id}/schedule-speaker`, '_blank')}
              sx={{ textTransform: 'none', borderColor: '#42A5F5', color: '#42A5F5' }}
            >
              Kör körschema
            </Button>
            <Button
              variant="outlined"
              startIcon={<TvIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`/workshopn/${id}/schedule-public`, '_blank')}
              sx={{ textTransform: 'none', borderColor: accent, color: accent }}
            >
              Öppna helskärm
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving}
              sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}
            >
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Spara'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Empty state */}
      {blocks.length === 0 && !generating && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.disabled' }}>
          <AutoAwesomeIcon sx={{ fontSize: 40, mb: 2, opacity: 0.3 }} />
          <Typography>Klicka "Generera" för att skapa ett körschema från briefen.</Typography>
        </Box>
      )}
    </Box>
  );
}
